import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, input, inject } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { AsYouType } from 'libphonenumber-js';
import { NgControlBaseComponent } from 'projects/components/util/ng-control-base.component';
import { CallbackWrapperService } from 'projects/login/src/app/services/callback-wrapper.service';
import { ImageAssetPipe } from 'projects/login/src/app/utils/image/image-asset.pipe';
import { SkeletonLoaderDirective } from 'projects/login/src/app/utils/skeleton-loader.directive';
import { noop } from 'rxjs';
import { FieldMultiValidatorComponent } from '../field-multi-validator/field-multi-validator.component';
import { phoneCountries } from './models/countries-data';

@Component({
  selector: 'pay-phone-input',
  imports: [FormsModule, FieldMultiValidatorComponent, NgClass, SkeletonLoaderDirective],
  templateUrl: './phone-input.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PhoneInputComponent extends NgControlBaseComponent implements ControlValueAccessor, AfterViewInit, OnInit, OnDestroy {
  override ngControl: NgControl;
  override readonly cdRef: ChangeDetectorRef;
  override readonly callbackService: CallbackWrapperService;

  readonly name = input<string>('');
  readonly label = input<string>('');
  readonly required = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly placeholder = input<string>('');
  readonly helperText = input('');
  readonly country = input<string>();
  readonly formSubmitted = input<boolean>(false);
  readonly size = input<'sm' | 'md'>('md');
  countries = phoneCountries;

  @Output() countryChanged = new EventEmitter<string>();
  value: string = '';
  selectedCountry: string = 'NL'; // Default country
  imageSource: string = '';

  onChange: (value: string) => void = noop;
  onTouch: () => void = noop;
  private _imageAssetPipe: ImageAssetPipe;

  constructor() {
    const ngControl = inject(NgControl);
    const cdRef = inject(ChangeDetectorRef);
    const callbackService = inject(CallbackWrapperService);

    super(cdRef, callbackService, ngControl);
    this.ngControl = ngControl;
    this.cdRef = cdRef;
    this.callbackService = callbackService;

    ngControl.valueAccessor = this;
    this._imageAssetPipe = new ImageAssetPipe();
  }

  writeValue(input: string): void {
    this.value = input;
  }

  ngAfterViewInit(): void {
    // Initialize with a country code
    this.onCountryCodeSelectChange(this.selectedCountry);

    const country = this.country();
    if (country) {
      this.selectedCountry = country;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  onInputChange(input: string): void {
    this.value = new AsYouType().input(input);
    this.handleCountryCode();

    // Call the registered onChange callback with the new value
    this.onChange(this.value);
    this.onTouch();
  }

  onCountryCodeSelectChange(countryCode: string): void {
    this.countryChanged.emit(countryCode);
    this.value = this.getCountryPrefix(countryCode);
    this.imageSource = this._imageAssetPipe.transform(countryCode.toLowerCase(), 'countries');
    this.onInputChange(this.value);
  }

  handleCountryCode() {
    const currentCountryCode = this.getCountryCodeFromInput();
    if (currentCountryCode) {
      this.selectedCountry = currentCountryCode.code;
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const validKeys = ['backspace', 'tab', 'enter', 'control', 'meta', 'arrowright', 'arrowleft', 'f5', 'f11'];
    // Get the key code of the pressed key
    const key = event?.key?.toLowerCase();
    if (validKeys.includes(key)) {
      return;
    }

    // Check if the pressed key is a letter or special character
    if (/[^+\d]/.test(key)) {
      event.preventDefault();
      return;
    }

    if (this.value === phoneCountries.find((country) => country.code === this.selectedCountry)?.prefix) {
      this.value += ' ';
    }
  }

  getFlagImageUrl(code: string) {
    return phoneCountries.find((x) => x.code === code)?.flagUrl ?? 'ZZ.svg';
  }

  selectKeyDown($event: KeyboardEvent) {
    // Allow user to press right arrow to start typing phone number
    if ($event.key.toLowerCase() === 'arrowright') {
      $event.preventDefault();
      document.getElementById(this.name())?.focus();
    }
  }

  private getCountryCodeFromInput() {
    const phoneNumberPrefix = this.value.substring(0, 3);
    return phoneCountries.find((country) => country.prefix === phoneNumberPrefix);
  }

  private getCountryPrefix(countryCode: string): string {
    const country = phoneCountries.find((country) => country.code === countryCode);
    return country ? country.prefix : '';
  }
}
